import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { TransportRequestConfig } from '@mangovoice/api-client'
import {
  ExtensionInstance,
  ExtensionType,
  PaginatedApiResult,
} from '@mangovoice/types'

import type { SwitchboardApi } from '../api/switchboardApi'
import type { ApiError } from '../api/types'

const SWITCHBOARD_EXTENSION_TYPES = new Set<string>(
  Object.values(ExtensionType),
)

const isSwitchboardExtension = (extension: ExtensionInstance) =>
  SWITCHBOARD_EXTENSION_TYPES.has(extension.type) ||
  extension.voicemail_enabled === 1 ||
  // Some legacy extension payloads represent voicemail status as a boolean.
  (extension.voicemail_enabled as unknown) === true

interface UseGetSwitchboardExtensionsParams {
  api: Pick<SwitchboardApi, 'listExtensions'>
  pbxId: number | null
  requestConfig?: TransportRequestConfig
}

export const useGetSwitchboardExtensions = ({
  api,
  pbxId,
  requestConfig,
}: UseGetSwitchboardExtensionsParams) => {
  const { data: extensionsResponse, isLoading } = useQuery<
    PaginatedApiResult<ExtensionInstance> | null,
    ApiError
  >({
    queryKey: ['extensions', pbxId, 'switchboard-types'],
    queryFn: async () => {
      if (!pbxId) return null

      const allExtensions: ExtensionInstance[] = []
      let offset = 0
      const batchSize = 500
      let hasMore = true

      while (hasMore) {
        const response = await api.listExtensions(
          {
            pbx_id: pbxId,
            ordering: 'number',
            limit: batchSize,
            offset,
          },
          requestConfig,
        )

        if (!response.ok || !response.data) break

        const result = response.data
        if (!result.results) break

        allExtensions.push(...result.results)
        hasMore = !!result.next

        if (hasMore) {
          let nextOffset: number | null = null

          if (result.next) {
            try {
              const url = result.next.startsWith('http')
                ? new URL(result.next)
                : new URL(result.next, 'https://api.mangovoice.com')
              const offsetParam = url.searchParams.get('offset')
              if (offsetParam) {
                const parsedOffset = parseInt(offsetParam, 10)
                if (!Number.isNaN(parsedOffset) && parsedOffset > offset) {
                  nextOffset = parsedOffset
                }
              }
            } catch {
              nextOffset = null
            }
          }

          nextOffset ??=
            result.results.length > 0 ? offset + result.results.length : null

          if (nextOffset === null) break
          offset = nextOffset
        }
      }

      const extensionMap = new Map<number, ExtensionInstance>()
      allExtensions.filter(isSwitchboardExtension).forEach((extension) => {
        if (extension.id) {
          extensionMap.set(extension.id, extension)
        }
      })

      const sortedExtensions = Array.from(extensionMap.values()).sort(
        (first, second) => {
          const firstNumber = parseInt(first.number || '0', 10) || 0
          const secondNumber = parseInt(second.number || '0', 10) || 0
          return firstNumber - secondNumber
        },
      )

      return {
        count: sortedExtensions.length,
        next: null,
        previous: null,
        results: sortedExtensions,
      }
    },
    enabled: !!pbxId,
    retry: false,
  })

  const extensionsList = useMemo(
    () => extensionsResponse?.results || [],
    [extensionsResponse],
  )

  const extensionMap = useMemo(
    () =>
      new Map(
        extensionsList
          .filter((extension) => !!extension.id)
          .map((extension) => [extension.id, extension]),
      ),
    [extensionsList],
  )

  return {
    extensionMap,
    extensionsList,
    isLoading,
  }
}
